import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from '../../../shared/ss-select-tooltip.directive';

import type { DesignNode, TextDataBinding } from '../../design-builder.types';
import { DesignDataService, STATUS_COUNT_AGGREGATION, formatBoundValue } from '../../services/design-data.service';
import { DesignDocumentStore } from '../../state/design-document.store';
import { DesignBuildHostComponent } from '../../data/design-build-host.component';
import { isTextFormula } from '../../data/design-runtime.service';
import { ZoneSliderDataComponent } from './zone-slider-data.component';
import { InputBoxDataComponent } from './input-box-data.component';
import { EMPTY_FILTER_OPERATORS, withEmptyFilterOperators } from '../../../components/dropdown-step/dropdown-options.utils';
import type { DashboardTile } from '../../../dvm-layout-builder/dvm-layout-builder.types';

export interface DataColumnPick {
  dataset: string;
  field: string;
  aggregation: string;
}

/**
 * Right-pane "Data" tab: Dataset → Column → Summary dropdowns, loaded through
 * the same calls the DVM Layout Builder uses. With a text box selected the
 * choice is applied to it live; otherwise the tab is a browser with a preview.
 */
@Component({
  selector: 'app-design-data-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, NgSelectModule, SsSelectTooltipDirective, DesignBuildHostComponent, ZoneSliderDataComponent, InputBoxDataComponent],
  templateUrl: './design-data-panel.component.html',
  styleUrl: './design-data-panel.component.scss',
})
export class DesignDataPanelComponent {
  protected readonly data = inject(DesignDataService);
  private readonly doc = inject(DesignDocumentStore);
  readonly statusCount = STATUS_COUNT_AGGREGATION;

  /**
   * The departments to pick from — a computed, so the SAME array is handed to
   * the dropdown until a new answer arrives. Calling the service in the
   * template built a new array every change detection; ng-select reset its
   * list on each one, so the click on an option was lost and nothing selected.
   */
  readonly statusChoices = computed(() => this.data.statusChoices(this.selectedText()?.dataBinding));

  /** Status / count column / click-following of the selected text's link. */
  patchTextBinding(patch: Partial<TextDataBinding>): void {
    const text = this.selectedText();
    if (!text?.dataBinding) return;
    this.doc.updateNode(text.id, { dataBinding: { ...text.dataBinding, ...patch } }, 'Text data');
  }

  /** The selected node, when exactly one is selected. */
  readonly selected = input<DesignNode | null>(null);
  readonly columnPicked = output<DataColumnPick>();
  readonly unbind = output<void>();
  /** Register the selected text as a compute component. */
  readonly connectText = output<{ nodeId: string; binding: TextDataBinding }>();
  /** A chart / table / control was generated through the build form. */
  readonly widgetBuilt = output<{ nodeId: string; tile: DashboardTile }>();
  /** A widget with its own data form (zone slider) changed its `binding`. */
  readonly widgetBindingChange = output<{ nodeId: string; binding: Record<string, unknown> }>();
  /** Zone slider: Generate / Update Configuration pressed. */
  readonly widgetGenerate = output<{ nodeId: string; binding: Record<string, unknown> }>();

  readonly selectedWidget = computed(() => {
    const node = this.selected();
    return node?.type === 'WIDGET' && node.widget ? node : null;
  });

  /** Bound client-side from dataset rows, not through the Layout Builder's build form. */
  readonly selectedZoneSlider = computed(() => {
    const node = this.selectedWidget();
    return node?.widget?.layoutType === 'ZONE_SLIDER' ? node : null;
  });

  /** A dropdown: it can be a "Currency exchange" (its value multiplies its targets). */
  readonly selectedDropdown = computed(() => {
    const node = this.selectedWidget();
    return node?.widget?.layoutType === 'DROPDOWN' ? node : null;
  });

  /** "Currency exchange" ticked: the selected value is a rate its targets are shown ×, not a filter. */
  setCurrencyExchange(node: DesignNode, on: boolean): void {
    if (!node.widget) return;
    const appearance: Record<string, unknown> = { ...(node.widget.appearance ?? {}) };
    if (on) appearance['currencyExchange'] = true;
    else delete appearance['currencyExchange'];
    this.doc.updateNode(node.id, { widget: { ...node.widget, appearance } }, 'Currency exchange');
  }

  /** "Show the value only" ticked: picking a value just shows it - nothing is filtered or refreshed. */
  setValueOnly(node: DesignNode, on: boolean): void {
    if (!node.widget) return;
    const appearance: Record<string, unknown> = { ...(node.widget.appearance ?? {}) };
    // Unticked is kept (false): Update Configuration then leaves it a dashboard-wide filter.
    appearance['valueOnly'] = on;
    this.doc.updateNode(node.id, { widget: { ...node.widget, appearance } }, 'Show the value only');
  }

  /** An input box: "Fill from data" above its build form. */
  readonly selectedInputBox = computed(() => {
    const node = this.selectedWidget();
    return node?.widget?.layoutType === 'INPUT_BOX' ? node : null;
  });

  /** Configured entirely in the Design tab — no dataset, no Generate. */
  readonly selectedButton = computed(() => {
    const node = this.selectedWidget();
    return node?.widget?.layoutType === 'BUTTON' ? node : null;
  });

  /** What the build host loads: every widget except those with their own form. */
  readonly buildNode = computed(() => (this.selectedZoneSlider() || this.selectedButton() ? null : this.selectedWidget()));

  readonly dataset = signal('');
  readonly field = signal('');
  readonly aggregation = signal('SUM');

  readonly selectedText = computed(() => {
    const node = this.selected();
    return node?.type === 'TEXT' ? node : null;
  });

  readonly datasetOptions = computed(() => {
    const state = this.data.datasets();
    return state.status === 'ready' ? state.value : [];
  });

  readonly columnState = computed(() => (this.dataset() ? this.data.columns(this.dataset()) : null));
  readonly columnOptions = computed(() => {
    const state = this.columnState();
    return state?.status === 'ready' ? state.value : [];
  });

  /** The componentId this text is registered under, once connected. */
  readonly componentId = computed(() => this.selectedText()?.dataBinding?.componentId ?? '');
  readonly canConnect = computed(() => !!this.selectedText() && !!this.dataset() && !!this.field());

  // The text's own link (componentId, status, format…) with the dropdowns on
  // top. Leaving the componentId out — as before — meant the value it is
  // stored under was never found, so the preview said "Loading…" forever.
  private readonly draft = computed<TextDataBinding>(() => ({
    ...(this.selectedText()?.dataBinding ?? {}),
    dataset: this.dataset(),
    field: this.field(),
    aggregation: this.aggregation(),
  }));

  readonly preview = computed<{ text: string; error: boolean }>(() => {
    const binding = this.draft();
    if (!binding.dataset) return { text: '', error: false };
    if (!binding.field) return { text: 'Pick a column.', error: false };
    const state = this.data.value(binding, this.selectedText()?.id ?? '');
    if (state.status === 'ready') return { text: formatBoundValue(binding, state.value), error: false };
    if (state.status === 'error') return { text: state.message, error: true };
    if (state.status === 'loading') return { text: 'Loading…', error: false };
    return { text: 'Not loaded yet — press Compute.', error: false };
  });

  constructor() {
    this.data.loadDatasets();

    // Follow the selection: a bound text fills the dropdowns with its binding.
    effect(() => {
      const text = this.selectedText();
      const binding = text?.dataBinding;
      untracked(() => {
        // No text selected → clear, or the panel keeps the last one's dataset
        // and column and offers a Compute button with nothing to compute.
        if (!text) {
          this.dataset.set('');
          this.field.set('');
          this.aggregation.set('SUM');
          return;
        }
        this.dataset.set(binding?.dataset ?? '');
        this.field.set(binding?.field ?? '');
        this.aggregation.set(binding?.aggregation ?? 'SUM');
        if (binding?.dataset) this.loadDataset(binding.dataset);
      });
    });
  }

  onDatasetChange(dataset: string | null): void {
    const next = dataset ?? '';
    if (next === this.dataset()) return;
    this.dataset.set(next);
    this.field.set('');
    if (next) this.loadDataset(next);
    else if (this.selectedText()?.dataBinding) this.unbind.emit();
  }

  onFieldChange(field: string | null): void {
    this.field.set(field ?? '');
    this.apply();
    this.loadStatusChoices();
  }

  /** The value is a formula over columns (kept in `field`), not one column. */
  readonly isFormula = computed(() => isTextFormula(this.field()));

  /**
   * A formula instead of a Column: the Summary is applied to it per row, e.g.
   * SUM of "[target_price] - [set_price]". Applied on change (blur / Enter), not
   * on every keystroke, so a half-typed formula is never sent to compute.
   */
  onFormulaChange(formula: string): void {
    const next = String(formula ?? '').trim();
    if (!next && !this.isFormula()) return;
    if (next && !isTextFormula(next)) {
      // A single name is just a column.
      this.onFieldChange(next);
      return;
    }
    if (this.aggregation() === STATUS_COUNT_AGGREGATION) this.aggregation.set('SUM');
    this.onFieldChange(next);
  }

  /** "Only rows where": the column to test; clearing it drops the filter. */
  onRowFilterFieldChange(field: string | null): void {
    this.patchTextBinding(field
      ? { filterField: field, filterValues: [] }
      : { filterField: undefined, filterOperator: undefined, filterValues: undefined });
  }

  /** "Only rows where" operators: a dropdown's / table column's, plus "is (not) empty". */
  readonly rowFilterOperators = withEmptyFilterOperators();

  /** "Is (not) empty" needs no value. */
  rowFilterNeedsValue(bound: TextDataBinding): boolean {
    return !EMPTY_FILTER_OPERATORS.some((option) => option.value === bound.filterOperator);
  }

  /** The operator: switching keeps the typed values ("in" lists them, the others use the first). */
  onRowFilterOperatorChange(operator: string | null): void {
    this.patchTextBinding({ filterOperator: operator && operator !== 'in' ? operator : undefined });
  }

  /** "…is one of": the values a row's filter column must have to count. */
  onRowFilterValuesChange(values: unknown): void {
    const list = (Array.isArray(values) ? values : [])
      .map((value) => String(typeof value === 'object' && value !== null ? (value as { label?: unknown }).label ?? '' : value).trim())
      .filter(Boolean);
    this.patchTextBinding({ filterValues: [...new Set(list)] });
  }

  /** "Round off" choices; `autoDecimals` = the default format (no fixed rounding). */
  readonly autoDecimals = 'auto';
  readonly roundOffOptions: Array<{ value: number | 'auto'; label: string }> = [
    { value: 'auto', label: 'As is (default)' },
    { value: 0, label: 'Whole number (0 decimals)' },
    { value: 1, label: '1 decimal' },
    { value: 2, label: '2 decimals' },
    { value: 3, label: '3 decimals' },
  ];

  onRoundOffChange(value: number | 'auto' | null): void {
    this.patchTextBinding({ decimals: typeof value === 'number' ? value : undefined });
  }

  onAggregationChange(aggregation: string | null): void {
    this.aggregation.set(aggregation || 'SUM');
    this.apply();
    this.loadStatusChoices();
  }

  /** The tick under Column: count per value (STATUS_COUNTS) instead of a Summary. */
  onStatusCountToggle(on: boolean): void {
    this.onAggregationChange(on ? STATUS_COUNT_AGGREGATION : 'SUM');
  }

  /** "Count per status": ask the API at once, so its departments fill the dropdown. */
  private loadStatusChoices(): void {
    if (this.aggregation() !== STATUS_COUNT_AGGREGATION || !this.canConnect()) return;
    // After the binding above is written, so the call carries the new choice.
    queueMicrotask(() => this.connect());
  }

  reload(): void {
    this.data.loadDatasets(true);
  }

  private loadDataset(dataset: string): void {
    this.data.loadColumns(dataset);
  }

  /** Register the text as a KPI component and read its value from compute. */
  connect(): void {
    const text = this.selectedText();
    if (!text || !this.canConnect()) return;
    this.connectText.emit({
      nodeId: text.id,
      binding: { dataset: this.dataset(), field: this.field(), aggregation: this.aggregation() },
    });
  }

  private apply(): void {
    if (!this.selectedText() || !this.dataset() || !this.field()) return;
    this.columnPicked.emit({ dataset: this.dataset(), field: this.field(), aggregation: this.aggregation() });
  }
}

